import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import useTranslation from "../../i18n/useTranslation";
import {getLocalizedGraphQLError} from "../../util/graphqlError";
import GetUserProfiles from "../team/queries/GetUserProfiles";
import AddPlayerToTeamSeasonMutation from "../team/mutations/AddPlayerToTeamSeason";
import GetReferees from "../GetReferees";

const ROLE_MANAGER = 6;

export default function AddStaffMemberModal({
    show,
    onClose,
    profileId,
    organizations = [],
    colors,
    onInvited,
    role = ROLE_MANAGER,
    isReferee = false,
}) {
    const { t: translate } = useTranslation();
    const t = (key, options) => key.startsWith("staffPicker.")
        ? translate(`${isReferee ? "refereePicker" : "staffPicker"}.${key.substring("staffPicker.".length)}`, options)
        : translate(key, options);
    const [getUserProfiles, { data, loading, error }] = GetUserProfiles();
    const [getReferees, refereeQuery] = GetReferees();
    const [invitePlayer, { loading: isInviting }] = AddPlayerToTeamSeasonMutation();
    const profileQuery = isReferee ? refereeQuery : {data, loading, error};
    const profileData = profileQuery.data;
    const profileLoading = profileQuery.loading;
    const profileError = profileQuery.error;

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [invitingProfileId, setInvitingProfileId] = useState(null);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
    const [optimisticAddedKeys, setOptimisticAddedKeys] = useState([]);
    const lastSearchTermRef = useRef(null);
    const lastRequestKeyRef = useRef("");

    const pageSize = 10;

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (!show) return;

        const firstOrganizationId = Number(organizations?.[0]?.id) || null;
        setSelectedOrganizationId((prev) => prev || firstOrganizationId);
    }, [organizations, show]);

    useEffect(() => {
        if (!show || !profileId) return;

        if (lastSearchTermRef.current !== debouncedSearchTerm) {
            lastSearchTermRef.current = debouncedSearchTerm;
            if (page !== 1) {
                setPage(1);
                return;
            }
        }

        const requestKey = `${profileId}:${debouncedSearchTerm}:${page}`;
        if (lastRequestKeyRef.current === requestKey) return;
        lastRequestKeyRef.current = requestKey;

        const loadProfiles = isReferee ? getReferees : getUserProfiles;
        loadProfiles({
            variables: {
                q: debouncedSearchTerm || null,
                page,
                pageSize,
            },
            fetchPolicy: "network-only",
        });
    }, [debouncedSearchTerm, getReferees, getUserProfiles, isReferee, page, pageSize, profileId, show]);

    useEffect(() => {
        if (!show) {
            setSearchTerm("");
            setDebouncedSearchTerm("");
            setPage(1);
            lastSearchTermRef.current = null;
            lastRequestKeyRef.current = "";
            setInvitingProfileId(null);
            setSelectedOrganizationId(null);
            setOptimisticAddedKeys([]);
        }
    }, [show]);

    const profiles = useMemo(() => {
        const result = isReferee ? profileData?.referees : profileData?.sparePlayers;
        const rawProfiles = Array.isArray(result?.objects)
            ? result.objects
            : [];

        const seenIds = new Set();
        return rawProfiles.filter((profile) => {
            const currentProfileId = Number(profile?.id);
            if (!currentProfileId || seenIds.has(currentProfileId)) return false;
            seenIds.add(currentProfileId);
            return true;
        });
    }, [isReferee, profileData]);

    const pagination = useMemo(
        () => ({
            page: Number((isReferee ? profileData?.referees : profileData?.sparePlayers)?.page) || page,
            pages: Number((isReferee ? profileData?.referees : profileData?.sparePlayers)?.pages) || 1,
            hasNext: Boolean((isReferee ? profileData?.referees : profileData?.sparePlayers)?.hasNext),
            hasPrev: Boolean((isReferee ? profileData?.referees : profileData?.sparePlayers)?.hasPrev),
        }),
        [isReferee, page, profileData]
    );

    const optimisticAddedSet = useMemo(() => new Set(optimisticAddedKeys || []), [optimisticAddedKeys]);

    const isAlreadyAdded = (profile) => {
        const orgId = Number(selectedOrganizationId);
        const profileIdValue = Number(profile?.id);
        if (!orgId || !profileIdValue) return false;

        const optimisticKey = `${orgId}:${profileIdValue}`;
        if (optimisticAddedSet.has(optimisticKey)) return true;

        const profileInvites = Array.isArray(profile?.profileOrganizationsInvited)
            ? profile.profileOrganizationsInvited
            : [];

        return profileInvites.some((invite) => Number(invite?.organization?.id) === orgId);
    };

    const handleAdd = async (selectedProfile) => {
        const selectedProfileId = Number(selectedProfile?.id);
        const organizationId = Number(selectedOrganizationId);
        if (!profileId || !selectedProfileId || !organizationId) return;

        try {
            setInvitingProfileId(selectedProfileId);

            const { data: response } = await invitePlayer({
                variables: {
                    profileId: selectedProfileId,
                    teamId: organizationId,
                    seasonId: 0,
                    role,
                },
            });

            if (!response?.invitePlayer?.success) {
                toast.error(t("staffPicker.addError"), {
                    autoClose: 3000,
                    position: "top-center",
                    closeOnClick: true,
                });
                return;
            }

            const optimisticKey = `${organizationId}:${selectedProfileId}`;
            setOptimisticAddedKeys((prev) => {
                const next = new Set(prev || []);
                next.add(optimisticKey);
                return Array.from(next);
            });

            toast.success(t("staffPicker.addSuccess"), {
                autoClose: 2500,
                position: "top-center",
                closeOnClick: true,
            });

            if (onInvited) {
                onInvited();
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "staffPicker.addError"), {
                autoClose: 3000,
                position: "top-center",
                closeOnClick: true,
            });
        } finally {
            setInvitingProfileId(null);
        }
    };

    if (!show) return null;

    return (
        <div
            className="modal d-block"
            role="dialog"
            aria-modal="true"
            style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
        >
            <div className="modal-dialog modal-lg modal-dialog-scrollable kbola-existingStaffDialog" role="document">
                <div className="modal-content kbola-existingStaffModal">
                    <div
                        className="modal-header kbola-existingStaffHeader"
                        style={{
                            backgroundColor: colors?.primary,
                            color: colors?.textOnPrimary,
                        }}
                    >
                        <div className="kbola-existingStaffHeading"><span className="kbola-existingStaffIcon"><i className="fas fa-user-tie" /></span><div><span className="kbola-existingStaffEyebrow">{t("staffPicker.profile")}</span><h5 className="modal-title mb-0">{t("staffPicker.title")}</h5></div></div>
                        <button
                            type="button"
                            className="close"
                            onClick={onClose}
                            aria-label={t("common.cancel")}
                            disabled={isInviting}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div className="modal-body kbola-existingStaffBody">
                        <div className="kbola-existingStaffOrganization form-group">
                            <label className="mb-1">{t("staffPicker.organization")}</label>
                            <select
                                className="form-control kbola-existingStaffInput"
                                value={selectedOrganizationId || ""}
                                onChange={(e) => setSelectedOrganizationId(Number(e.target.value) || null)}
                                disabled={isInviting || !organizations.length}
                            >
                                {!organizations.length ? (
                                    <option value="">{t("staffPicker.noOrganizations")}</option>
                                ) : null}
                                {organizations.map((organization) => (
                                    <option key={organization.id} value={organization.id}>
                                        {organization.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="kbola-existingStaffSearch form-group">
                            <label>{t("staffPicker.searchPlaceholder")}</label>
                            <div className="input-group">
                                <div className="input-group-prepend">
                                    <span className="input-group-text" aria-hidden="true">
                                        <i className="fas fa-search" />
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control kbola-existingStaffInput"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t("staffPicker.searchPlaceholder")}
                                    disabled={isInviting}
                                />
                            </div>
                        </div>

                        {profileLoading ? (
                            <div className="text-center py-3">{t("common.loading")}</div>
                        ) : profileError ? (
                            <div className="alert alert-danger mb-0">{t("staffPicker.loadError")}</div>
                        ) : !profiles.length ? (
                            <div className="alert alert-light mb-0">
                                {debouncedSearchTerm
                                    ? t("staffPicker.noSearchResults")
                                    : t("staffPicker.noProfiles")}
                            </div>
                        ) : (
                            <div>
                                <div className="table-responsive kbola-existingStaffTableWrap">
                                    <table className="table mb-0 kbola-existingStaffTable">
                                        <thead>
                                            <tr>
                                                <th>{t("staffPicker.profile")}</th>
                                                <th className="text-right">{t("playersList.actions")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {profiles.map((profile) => {
                                                const profileKey = Number(profile.id);
                                                const alreadyAdded = isAlreadyAdded(profile);
                                                const isRowInviting = invitingProfileId === profileKey;

                                                return (
                                                    <tr key={profile.id}>
                                                        <td><div className="kbola-existingStaffProfile"><span className="kbola-existingStaffAvatar">{(profile.fullName || "?").trim().charAt(0).toUpperCase()}</span><div><strong>{profile.fullName}</strong>{profile.user?.email && <small>{profile.user.email}</small>}</div></div></td>
                                                        <td className="text-right">
                                                            <button
                                                                type="button"
                                                                className="btn btn-kbola btn-sm kbola-existingStaffAddButton"
                                                                onClick={() => handleAdd(profile)}
                                                                disabled={!selectedOrganizationId || alreadyAdded || isInviting}
                                                            >
                                                                {isRowInviting
                                                                    ? t("common.loading")
                                                                    : alreadyAdded
                                                                        ? t("staffPicker.alreadyAdded")
                                                                        : t("staffPicker.add")}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="d-flex align-items-center justify-content-between mt-3">
                                    <small className="text-muted">
                                        {t("teamMemberPicker.pageInfo", {
                                            page: pagination.page,
                                            pages: pagination.pages,
                                        })}
                                    </small>

                                    <div className="btn-group" role="group" aria-label="pagination">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                            disabled={!pagination.hasPrev || loading || isInviting}
                                        >
                                            {t("common.previous")}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setPage((prev) => prev + 1)}
                                            disabled={!pagination.hasNext || loading || isInviting}
                                        >
                                            {t("common.next")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer kbola-existingStaffFooter">
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={onClose}
                            disabled={isInviting}
                        >
                            {t("common.cancel")}
                        </button>
                    </div>
                </div>
            </div>
            <style>{`.kbola-existingStaffDialog{max-height:calc(100vh - 2rem)}.kbola-existingStaffModal{border:0;border-radius:18px;overflow:hidden;box-shadow:0 24px 70px rgba(20,21,26,.28)}.kbola-existingStaffHeader{display:flex;align-items:center;padding:23px 27px;background:linear-gradient(135deg,${colors?.primary},${colors?.secondary || colors?.primary});color:${colors?.textOnPrimary};border:0}.kbola-existingStaffHeading{display:flex;align-items:center;gap:13px}.kbola-existingStaffIcon{width:46px;height:46px;border-radius:14px;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.2);font-size:18px}.kbola-existingStaffEyebrow{display:block;font-size:10px;font-weight:900;letter-spacing:.12em;text-transform:uppercase;opacity:.75;margin-bottom:3px}.kbola-existingStaffHeader h5{font-size:20px;font-weight:900}.kbola-existingStaffHeader .close{color:inherit;opacity:.9}.kbola-existingStaffBody{padding:25px 27px;background:#f7f9fc;overflow-y:auto}.kbola-existingStaffOrganization,.kbola-existingStaffSearch{padding:16px;border:1px solid #e0e7ef;border-radius:14px;background:#fff;box-shadow:0 4px 12px rgba(20,21,26,.035)}.kbola-existingStaffSearch{margin-top:15px}.kbola-existingStaffOrganization label,.kbola-existingStaffSearch label{display:block;color:#4b5563;font-size:12px;font-weight:900;margin-bottom:8px}.kbola-existingStaffInput{height:42px;border:1px solid #d9e1ea;border-radius:10px;background:#fbfcfe;font-size:14px}.kbola-existingStaffSearch .input-group-text{border:1px solid #d9e1ea;border-right:0;border-radius:10px 0 0 10px;background:#fbfcfe;color:#8b98a8}.kbola-existingStaffSearch input{border-radius:0 10px 10px 0}.kbola-existingStaffInput:focus{border-color:#4aa8d2;box-shadow:0 0 0 3px rgba(74,168,210,.13)}.kbola-existingStaffTableWrap{margin-top:18px;border:1px solid #e0e7ef;border-radius:14px;background:#fff;overflow:hidden}.kbola-existingStaffTable thead{background:#f5f8fb}.kbola-existingStaffTable th{border:0;color:#778395;font-size:11px;font-weight:900;letter-spacing:.06em;text-transform:uppercase;padding:13px 16px}.kbola-existingStaffTable td{padding:13px 16px;border-top:1px solid #edf1f5;vertical-align:middle}.kbola-existingStaffProfile{display:flex;align-items:center;gap:11px}.kbola-existingStaffAvatar{width:36px;height:36px;border-radius:11px;display:flex;align-items:center;justify-content:center;background:#f0eaff;color:#7354c7;font-size:15px;font-weight:900}.kbola-existingStaffProfile strong{display:block;color:#1d2939;font-size:14px;font-weight:900}.kbola-existingStaffProfile small{display:block;color:#8b98a8;font-size:12px;margin-top:2px}.kbola-existingStaffAddButton{min-width:82px;border-radius:9px;font-weight:900}.kbola-existingStaffFooter{padding:15px 27px;border-top:1px solid #e5eaf0;background:#fff}.kbola-existingStaffFooter .btn-light{border:1px solid #d8e0e8;border-radius:10px;color:#526070;font-weight:800;padding:9px 16px}@media(max-width:700px){.kbola-existingStaffDialog{height:calc(100vh - 1rem);max-height:calc(100vh - 1rem);margin:.5rem auto}.kbola-existingStaffHeader{padding:20px 18px}.kbola-existingStaffBody{padding:18px}.kbola-existingStaffTable th,.kbola-existingStaffTable td{padding:11px 10px}.kbola-existingStaffProfile small{display:none}.kbola-existingStaffAddButton{min-width:70px;padding:7px 9px;font-size:12px}}`}</style>
        </div>
    );
}
